import { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";

const subjects = [
  "General enquiry",
  "Calculator feedback",
  "Partnership / media",
  "Bug report",
  "Something else",
];

export default function ContactPage() {
  useEffect(() => {
    const existing = document.getElementById("contact-schema");
    if (!existing) {
      const script = document.createElement("script");
      script.id = "contact-schema";
      script.type = "application/ld+json";
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact HANDICALC",
        description:
          "Get in touch with the HANDICALC team. Ask a question, send feedback or enquire about the Golf Shot Allowance Calculator.",
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById("contact-schema");
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary-900">
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950 via-primary-900 to-primary-800"></div>
          <div
            className="absolute inset-0 bg-cover bg-center opacity-10"
            style={{
              backgroundImage:
                "url('https://readdy.ai/api/search-image?query=Abstract%20golf%20course%20landscape%20with%20early%20morning%20mist%20rising%20over%20fairway%20and%20distant%20pine%20trees%20silhouette%2C%20soft%20painterly%20style%2C%20deep%20emerald%20green%20and%20muted%20sage%20colour%20palette%2C%20atmospheric%20editorial%20golf%20scene%20suitable%20as%20dark%20background%20overlay%20with%20text&width=1600&height=900&seq=contact-hero-bg&orientation=landscape')",
            }}
          ></div>
          <div className="relative mx-auto max-w-content px-4 md:px-6 pt-20 md:pt-28 pb-16 md:pb-24 text-center">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 backdrop-blur border border-background-50/20 text-xs font-medium text-accent-300">
              <i className="ri-mail-line"></i>
              Get in touch
            </span>
            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl mx-auto">
              Contact us
            </h1>
            <p className="mt-5 text-primary-100 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Have a question about the HANDICALC® golf shot allowance calculator, need help with WHS™ calculations, or want to discuss embedding the tool for your club or society? Send us a message and we will get back to you as soon as we can.
            </p>
          </div>
        </section>

        {/* Contact form + info */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
              {/* Info sidebar */}
              <aside className="lg:col-span-2 order-2 lg:order-1">
                <h2 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl leading-tight">
                  We would love to hear from you
                </h2>
                <p className="mt-4 text-foreground-700 text-sm md:text-base leading-relaxed">
                  HANDICALC® is the leading online golf shot allowance calculator built for golfers, club organisers, society secretaries and competition committees. Whether you need help calculating a WHS™ Playing Handicap, want to report a course data issue, or are interested in licensing HANDICALC® for your club website — we are here to help.
                </p>

                <div className="mt-10 space-y-6">
                  <div className="flex gap-4">
                    <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                      <i className="ri-mail-line text-accent-700 text-lg"></i>
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-950 text-sm">Email</h3>
                      <p className="mt-1 text-sm text-foreground-600">support@handicalc.com</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                      <i className="ri-timer-line text-accent-700 text-lg"></i>
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-950 text-sm">Response time</h3>
                      <p className="mt-1 text-sm text-foreground-600">We aim to reply within 24–48 hours</p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                      <i className="ri-earth-line text-accent-700 text-lg"></i>
                    </span>
                    <div>
                      <h3 className="font-semibold text-foreground-950 text-sm">Based in</h3>
                      <p className="mt-1 text-sm text-foreground-600">United Kingdom</p>
                    </div>
                  </div>
                </div>

                <div className="mt-10 rounded-xl bg-background-100 border border-background-200 p-5 md:p-6">
                  <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-3">
                    Common reasons people contact us
                  </h3>
                  <ul className="space-y-2 text-sm text-foreground-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-question-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Questions about WHS™ calculations or format allowances</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-flag-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Reporting incorrect course data (CR, SR, tee names)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-building-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Club and society licensing enquiries</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-bug-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Calculator bugs or feature suggestions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-trophy-line text-primary-600 mt-0.5" aria-hidden="true"></i>
                      <span>Questions about the Handicalc Challenge competition</span>
                    </li>
                  </ul>
                </div>


              </aside>

              {/* Form */}
              <div className="lg:col-span-3 order-1 lg:order-2">
                <form
                  action="https://formspree.io/f/xwpgnnyg"
                  method="POST"
                  className="rounded-2xl bg-background-50 border border-background-200 p-6 md:p-10"
                >
                  <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
                    Send us a message
                  </h2>
                  <p className="mt-2 text-sm text-foreground-600">
                    All fields marked with <span className="text-accent-600">*</span> are required
                  </p>

                  <div className="mt-8 space-y-5">
                    {/* Name */}
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Your name <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full rounded-xl border border-background-200 px-4 py-3 text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow"
                        placeholder="e.g. James Morrison"
                        maxLength={120}
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Email address <span className="text-accent-600">*</span>
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full rounded-xl border border-background-200 px-4 py-3 text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow"
                        placeholder="you@example.com"
                        maxLength={254}
                      />

                    </div>

                    {/* Subject */}
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Subject <span className="text-accent-600">*</span>
                      </label>
                      <select
                        id="contact-subject"
                        name="subject"
                        required
                        className="w-full rounded-xl border border-background-200 px-4 py-3 text-sm bg-background-50 text-foreground-950 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow appearance-none cursor-pointer"
                      >
                        <option value="" disabled selected>Select a subject</option>
                        {subjects.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Your message <span className="text-accent-600">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        rows={6}
                        required
                        className="w-full rounded-xl border border-background-200 px-4 py-3 text-sm bg-background-50 text-foreground-950 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400 transition-shadow resize-none"
                        placeholder="Tell us what you would like to know..."
                        maxLength={500}
                      />
                      <p className="mt-1.5 text-xs text-foreground-400">Maximum 500 characters</p>
                    </div>



                    {/* Submit */}
                    <div className="pt-2">
                      <button
                        type="submit"
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 hover:bg-primary-700 text-background-50 text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
                      >
                        Send message <i className="ri-send-plane-line"></i>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* Quick links CTA */}
        <section className="bg-background-100 py-16 md:py-24 border-y border-background-200">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Link
                to="/calculator"
                className="group rounded-2xl bg-background-50 border border-background-200 p-6 md:p-8 cursor-pointer transition-colors hover:border-primary-300"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-calculator-line text-xl"></i>
                </span>
                <h3 className="mt-5 font-heading font-semibold text-foreground-950 text-lg">
                  Try the calculator
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  Get your WHS™ Playing Handicap instantly — no sign-up needed.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 whitespace-nowrap">
                  Open calculator <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>

              <Link
                to="/faq"
                className="group rounded-2xl bg-background-50 border border-background-200 p-6 md:p-8 cursor-pointer transition-colors hover:border-primary-300"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className="ri-question-answer-line text-xl"></i>
                </span>
                <h3 className="mt-5 font-heading font-semibold text-foreground-950 text-lg">
                  Browse the FAQ
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  Your question might already be answered in our knowledge base.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-secondary-700 whitespace-nowrap">
                  Read the FAQ <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>

              <Link
                to="/formats"
                className="group rounded-2xl bg-background-50 border border-background-200 p-6 md:p-8 cursor-pointer transition-colors hover:border-primary-300"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                  <i className="ri-file-list-line text-xl"></i>
                </span>
                <h3 className="mt-5 font-heading font-semibold text-foreground-950 text-lg">
                  Explore formats
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  See all major playing formats and their official allowance percentages.
                </p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-accent-700 whitespace-nowrap">
                  View formats <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Bottom CTA */}
        <section className="bg-background-50 py-16 md:py-24">
          <div className="mx-auto max-w-content px-4 md:px-6">
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src="https://readdy.ai/api/search-image?query=Serene%20golf%20course%20fairway%20at%20golden%20hour%20with%20long%20shadows%20stretching%20across%20manicured%20green%2C%20single%20flagstick%20in%20distance%20against%20warm%20amber%20sky%2C%20painterly%20editorial%20style%2C%20rich%20emerald%20green%20and%20warm%20golden%20amber%20colour%20palette%2C%20cinematic%20composition%20suitable%20as%20dark%20background%20with%20text%20contrast&width=1600&height=800&seq=contact-cta-bg&orientation=landscape"
                alt="Golf course fairway at golden hour"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-950/90 via-primary-900/75 to-primary-800/60"></div>
              <div className="relative z-10 px-6 md:px-14 py-14 md:py-20 text-center max-w-3xl mx-auto">
                <h2 className="font-heading font-bold text-background-50 text-3xl md:text-4xl leading-tight">
                  Ready to calculate?
                </h2>
                <p className="mt-4 text-background-100/90 text-base md:text-lg leading-relaxed">
                  Pick your course, tee and format — and get your correct WHS™ Playing Handicap in seconds. No account needed, works on any device.
                </p>
                <div className="mt-8">
                  <Link
                    to="/calculator"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-accent-500 hover:bg-accent-600 text-foreground-950 text-sm md:text-base font-semibold whitespace-nowrap cursor-pointer transition-colors"
                  >
                    Open the calculator <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}